import { Component, Inject } from '@angular/core';
import {
  MatDialogRef,
  MAT_DIALOG_DATA,
  MatDialogTitle,
  MatDialogContent,
  MatDialogActions,
} from '@angular/material/dialog';
import {
  AddCatalogItemsDialogData,
  SelectProductsCatalogTab,
  SelectProductsTabConfig,
} from '@components/add-catalog-items-dialog/1.0';
import { BadgeType } from '@constants/badge/1.0';
import { ButtonStyle } from '@constants/button/1.0';
import { Composite } from '@generated/tcea/models';
import { CatalogItem } from '@interfaces/catalog/1.0/catalog-item';
import { Product } from '@interfaces/product/1.0';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { NgClass, NgFor } from '@angular/common';
import { ItemListComponent } from '../../item-list/1.0/item-list.component';
import { ButtonComponent } from '../../button/1.0/button.component';

type CatalogItemUnion = Product;

interface TabState {
  id: SelectProductsCatalogTab;
  labelKey: string;
  listTitleKey: string;
  items: CatalogItem[];
}

@Component({
  selector: 'txp-select-products-dialog',
  templateUrl: './select-products-dialog.component.html',
  styleUrls: ['./select-products-dialog.component.scss'],
  standalone: true,
  imports: [
    MatDialogTitle,
    CdkScrollable,
    MatDialogContent,
    NgClass,
    NgFor,
    ItemListComponent,
    MatDialogActions,
    ButtonComponent,
    TranslateModule,
  ],
})
export class SelectProductsDialogComponent {
  noneSelectedMsg: string;
  filterText: string;
  entitiesSelectedText: string;
  buttonStyle = ButtonStyle;
  badgeType = BadgeType; // expose enum to template
  productList: Product[];
  comboList: Composite[];
  selectedItems: CatalogItemUnion[] = [];
  stagedSelectedItems: CatalogItemUnion[];
  itemsList: CatalogItemUnion[];
  listTitle: string;
  tabs: TabState[] = [];
  selectedTab: SelectProductsCatalogTab = 'PRODUCT';
  persistSelectionAcrossTabs: boolean;
  returnItemTypes: boolean;
  doneButtonLabelKey: string;
  addFromLabelKey: string;
  disableSelectionReset: boolean;
  private selectedItemsById = new Map<
    string,
    CatalogItem & { itemType?: SelectProductsCatalogTab }
  >();

  constructor(
    private dialogRef: MatDialogRef<SelectProductsDialogComponent>,
    private translateService: TranslateService,
    @Inject(MAT_DIALOG_DATA) public data: AddCatalogItemsDialogData,
  ) {
    this.selectedItems = data?.selectedList?.slice() || [];
    this.persistSelectionAcrossTabs = data.persistSelectionAcrossTabs ?? false;
    this.returnItemTypes = data.returnItemTypes ?? false;
    this.disableSelectionReset = this.persistSelectionAcrossTabs;
    this.doneButtonLabelKey = data.doneButtonLabelKey || 'form.button.done';
    this.addFromLabelKey = data.addFromLabelKey || 'menu.createMenu.addFrom';

    this.productList = this.removeFromList(this.selectedItems, data.productList);
    this.comboList = this.removeFromList(this.selectedItems, data.comboList);
    this.tabs = this.buildTabs(data);
    this.selectedTab = this.tabs[0]?.id ?? 'PRODUCT';
    this.listTitle = this.getListTitle(this.selectedTab);
    this.filterText = this.getFilterText(this.selectedTab);
    this.entitiesSelectedText = this.getEntitiesSelectedText(this.selectedTab);

    this.noneSelectedMsg = translateService
      .instant('menu.common.noSelectedMsg')
      .replace(':items', data.translations.type);
  }

  get currentList(): CatalogItem[] {
    return this.tabs.find((tab) => tab.id === this.selectedTab)?.items ?? [];
  }

  get hasSelection(): boolean {
    return this.persistSelectionAcrossTabs
      ? this.selectedItemsById.size > 0
      : !!this.selectedItems?.length;
  }

  get currentTabSelectedIds(): string[] {
    if (!this.persistSelectionAcrossTabs) {
      return [];
    }

    return Array.from(this.selectedItemsById.values())
      .filter((item) => item.itemType === this.selectedTab)
      .map((item) => item.catalogEntryId);
  }

  get tabSelectedCount(): number {
    return this.currentTabSelectedIds.length;
  }

  get tabTotalCount(): number {
    return this.currentList.length;
  }

  get totalSelectedCount(): number {
    return this.selectedItemsById.size;
  }

  get totalItemsCount(): number {
    return this.tabs.reduce((sum, tab) => sum + tab.items.length, 0);
  }

  get tabSelectionSummary(): string {
    return this.translateService.instant('menu.common.tabSelectionSummary', {
      selected: this.tabSelectedCount,
      total: this.tabTotalCount,
      type: this.getTypeLabel(this.selectedTab).toLowerCase(),
    });
  }

  get totalSelectionSummary(): string {
    return this.translateService.instant('menu.common.totalSelectionSummary', {
      selected: this.totalSelectedCount,
      total: this.totalItemsCount,
    });
  }

  handleDone() {
    if (this.returnItemTypes) {
      this.dialogRef.close(
        Array.from(this.selectedItemsById.values()).map((item) => ({
          catalogEntryId: item.catalogEntryId,
          name: item.name,
          plu: (item as any).plu,
          itemType: item.itemType,
        })),
      );
      return;
    }

    this.dialogRef.close(this.selectedItems);
  }

  handleCancel() {
    this.dialogRef.close();
  }

  onSelected(selected: CatalogItemUnion[]) {
    this.stagedSelectedItems = selected;
    this.selectedItems = this.selectedItems.concat(this.stagedSelectedItems);
  }

  onSelectTab(tabId: SelectProductsCatalogTab) {
    if (this.selectedTab === tabId) {
      return;
    }

    this.selectedTab = tabId;
    this.listTitle = this.getListTitle(tabId);
    this.filterText = this.getFilterText(tabId);
    this.entitiesSelectedText = this.getEntitiesSelectedText(tabId);

    if (!this.persistSelectionAcrossTabs) {
      this.selectedItems = [];
    }
  }

  onSelectionChange(items: CatalogItem[]) {
    if (!this.persistSelectionAcrossTabs) {
      this.selectedItems = items as CatalogItemUnion[];
      return;
    }

    const currentTabIds = new Set(this.currentList.map((item) => item.catalogEntryId));
    currentTabIds.forEach((id) => {
      if (!items.find((item) => item.catalogEntryId === id)) {
        this.selectedItemsById.delete(id);
      }
    });

    items.forEach((item) => {
      this.selectedItemsById.set(item.catalogEntryId, {
        ...item,
        itemType: this.selectedTab,
      });
    });
  }

  /**
   * Removes all items in removeList from masterList without changing masterList. Returns
   * a new array without the removed items
   */
  removeFromList(removeList: any[], masterList: any[]) {
    const newList = masterList?.slice();
    if (!newList?.length) {
      return newList || [];
    }

    for (const item of removeList || []) {
      const index = newList.indexOf(item);
      if (index > -1) {
        newList.splice(index, 1);
      }
    }

    newList.sort((a, b) => (a.name > b.name ? 1 : -1));
    return newList;
  }

  getSelectedCatalog(eventData: CatalogItem[]) {
    this.selectedItems = eventData;
  }

  private buildTabs(data: AddCatalogItemsDialogData): TabState[] {
    if (data.catalogTabs?.length) {
      return data.catalogTabs.map((tab) => this.createTabState(tab, data));
    }

    return [
      this.createTabState(
        {
          id: 'PRODUCT',
          labelKey: 'menu.createMenu.products',
          listTitleKey: data.translations.listTitle,
        },
        data,
      ),
      this.createTabState(
        {
          id: 'COMBO',
          labelKey: 'menu.createMenu.combos',
          listTitleKey: 'store.limits.combos',
        },
        data,
      ),
    ];
  }

  private createTabState(tab: SelectProductsTabConfig, data: AddCatalogItemsDialogData): TabState {
    return {
      id: tab.id,
      labelKey: tab.labelKey,
      listTitleKey: tab.listTitleKey,
      items: this.removeFromList(this.selectedItems, this.getListForTab(tab.id, data)),
    };
  }

  private getListForTab(
    tabId: SelectProductsCatalogTab,
    data: AddCatalogItemsDialogData,
  ): CatalogItem[] {
    switch (tabId) {
      case 'CONTAINER':
        return data.containerList || [];
      case 'COMBO':
        return data.comboList || [];
      case 'MODIFIER':
        return data.modifierList || [];
      default:
        return data.productList || [];
    }
  }

  private getListTitle(tabId: SelectProductsCatalogTab): string {
    const tab = this.tabs.find((entry) => entry.id === tabId);
    if (!tab) {
      return this.data.translations.listTitle;
    }

    if (tab.listTitleKey === this.data.translations.listTitle) {
      return tab.listTitleKey;
    }

    return this.translateService.instant(tab.listTitleKey);
  }

  private getTypeLabel(tabId: SelectProductsCatalogTab): string {
    const tab = this.tabs.find((entry) => entry.id === tabId);
    if (tab && tab.listTitleKey !== this.data.translations.listTitle) {
      return this.translateService.instant(tab.listTitleKey);
    }

    return this.data.translations.type;
  }

  private getEntitiesSelectedText(tabId: SelectProductsCatalogTab): string {
    return `${this.getTypeLabel(tabId).toLowerCase()} ${this.translateService.instant('menu.common.selected')}`;
  }

  private getFilterText(tabId: SelectProductsCatalogTab): string {
    return `${this.translateService.instant('menu.common.search')} ${this.getTypeLabel(tabId)}`;
  }
}
